import { minutesSinceMidnight } from "@/lib/clock";
import { DAY_KEYS, type DayKey } from "@/lib/days";
import type { SleepLogConfig, SleepLogDayData } from "@/lib/schema";
import { minutesLate } from "./time";

export interface HalfResult {
  points: number;
  /** Minutes past the target, or null when the time read as early. */
  lateMinutes: number | null;
  logged: boolean;
}

const UNLOGGED: HalfResult = { points: 0, lateMinutes: null, logged: false };

/**
 * Score one half of a night. Every started interval past the target costs
 * one reduction, which is why this ceils: at a 15-minute interval, 00:15 is
 * still the first step (one reduction) and 00:16 opens the second.
 */
export function evaluateHalf(
  config: SleepLogConfig,
  target: string,
  logged: unknown,
): HalfResult {
  const targetMinutes = minutesSinceMidnight(target);
  const loggedMinutes = minutesSinceMidnight(logged);
  if (targetMinutes === null || loggedMinutes === null) return UNLOGGED;

  const late = minutesLate(targetMinutes, loggedMinutes);
  if (late === null)
    return { points: config.points, lateMinutes: null, logged: true };

  // Guards the settings preview, which renders while the interval field is
  // mid-edit and momentarily empty; validation blocks saving such a config.
  if (!Number.isFinite(config.intervalMinutes) || config.intervalMinutes <= 0) {
    return { points: config.points, lateMinutes: late, logged: true };
  }

  const steps = Math.ceil(late / config.intervalMinutes);
  const points = Math.max(
    0,
    config.points - config.reductionPerInterval * steps,
  );
  return { points, lateMinutes: late, logged: true };
}

export function scoreDay(config: SleepLogConfig, dayData: unknown): number {
  const data = (dayData ?? {}) as SleepLogDayData;
  return (
    evaluateHalf(config, config.asleepTarget, data.asleepAt).points +
    evaluateHalf(config, config.awakeTarget, data.awokeAt).points
  );
}

export function scoreWeek(
  config: SleepLogConfig,
  runtime: { week: unknown; days: Record<DayKey, unknown> },
): number {
  let total = 0;
  for (const k of DAY_KEYS) total += scoreDay(config, runtime.days[k]);
  return total;
}

/** Both halves score every day, so the ceiling is two full awards per day. */
export function maxScoreWeek(config: SleepLogConfig): number {
  return 7 * 2 * config.points;
}
